import { COLORS } from "@styles";
import { capitalizeFirstLetter, formatMoney } from "@utils";
import { Fragment, useMemo } from "react";
import { Divider, Typography } from "@mui/material";
import { Invoice, OrganizationBilling } from "@graphql";
import { formatDate, getInvoiceBillingDate } from "../../../utils";

const styles = {
  invoiceDetailsContainer: {
    display: 'flex',
    flexDirection: 'column',
    color: COLORS.NEUTRAL_LIGHT_3,
    border: `1px solid ${COLORS.BORDER_DARK}`,
    borderRadius: '1rem',
  } as React.CSSProperties,
  invoiceDetailsItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1rem',
  },
  invoiceDetailsDivider: {
    borderColor: COLORS.BORDER_DARK,
  },
}

type AmountField = keyof Pick<Invoice, 'amountRemaining' | 'amountDue' | 'amountPaid'>;

interface InvoiceDetailsProps {
  invoice?: Invoice | null;
  organizationBilling?: OrganizationBilling;
  amountField?: AmountField;
}

const mapInvoiceData = (invoice: Invoice, organizationBilling: OrganizationBilling, amountField: AmountField) => [
  {
    label: 'Invoice',
    value: `Invoice #${invoice?.number}`,
  },
  {
    label: 'Payment Date',
    value: formatDate(getInvoiceBillingDate(invoice as Invoice)),
  },
  {
    label: 'Payment Method',
    value: `${capitalizeFirstLetter(organizationBilling?.paymentMethod?.brand)} •••• ${organizationBilling?.paymentMethod?.last4}`,
  },
  {
    label: 'Amount',
    value: `${formatMoney(invoice?.[amountField] || 0)} CAD`,
  }
]

export function InvoiceDetails({ invoice, organizationBilling, amountField }: InvoiceDetailsProps) {
  const mappedData = useMemo(
    () => mapInvoiceData(invoice as Invoice, organizationBilling as OrganizationBilling, amountField || 'amountRemaining'),
    [invoice, organizationBilling, amountField]
  );

  return (
    <div style={styles.invoiceDetailsContainer}>
      {mappedData.map((item, index) => (
        <Fragment key={item.label}>
          <div style={styles.invoiceDetailsItem}>
            <Typography variant='body1' fontSize="0.875rem">{item.label}</Typography>
            <Typography variant='body1' fontSize="0.875rem">{item.value}</Typography>
          </div>
          {index !== mappedData.length - 1 && <Divider style={styles.invoiceDetailsDivider} flexItem variant="middle" />}
        </Fragment>
      ))}
    </div>
  );
}
